import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const CHUNK_SIZE = 4 * 1024 * 1024; // stays under the 10MB body limit + proxy limits

/** Upload a list of {stem, index, part} chunk tasks with limited concurrency,
 *  one retry each, reporting byte-level progress. */
async function uploadChunks(uploadId, tasks, onProgress) {
  const loaded = {};
  const totalBytes = tasks.reduce((s, t) => s + t.part.size, 0);
  const report = () => {
    const sum = Object.values(loaded).reduce((a, b) => a + b, 0);
    onProgress(Math.min(100, Math.round((sum / totalBytes) * 100)));
  };
  let cursor = 0;
  const worker = async () => {
    while (cursor < tasks.length) {
      const t = tasks[cursor++];
      const key = `${t.stem}:${t.index}`;
      const send = () => {
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('stem', t.stem);
        fd.append('index', String(t.index));
        fd.append('chunk', t.part, `${t.stem}.${t.index}.mp3`);
        return api.uploadStudioExportChunk(fd, (e) => {
          loaded[key] = Math.min(e.loaded, t.part.size);
          report();
        });
      };
      try {
        await send();
      } catch (err) {
        loaded[key] = 0;
        await send(); // one retry for flaky connections
      }
      loaded[key] = t.part.size;
      report();
    }
  };
  await Promise.all(Array.from({ length: Math.min(3, tasks.length) }, worker));
}

export function ExportDialog({ open, onOpenChange, engine, onSaved }) {
  const [name, setName] = useState('');
  const [phase, setPhase] = useState(null); // 'rendering' | 'encoding' | 'uploading' | null
  const [progress, setProgress] = useState(0);
  const { user } = useAuth();

  const handleExport = async () => {
    if (user?.is_demo) {
      toast.info("Demo Mode — exporting is disabled on the demo account. Sign up as an artist to save your stems.");
      return;
    }
    if (!name.trim()) { toast.error('Give this session a name'); return; }
    setPhase('rendering');
    try {
      const { vocalBlob, instrumentalBlob, mixdownBlob, reusedInstrumentalId } =
        await engine.renderStems({ onPhase: setPhase });
      if (!mixdownBlob || (!vocalBlob && !instrumentalBlob && !reusedInstrumentalId)) {
        toast.error('Load an instrumental or record a take before exporting.');
        return;
      }
      setPhase('uploading');
      const { data: session } = await api.beginStudioExport();

      const stems = [['mixdown', mixdownBlob]];
      if (vocalBlob) stems.push(['vocal', vocalBlob]);
      if (instrumentalBlob) stems.push(['instrumental', instrumentalBlob]);
      const tasks = [];
      const chunkCounts = {};
      for (const [stem, blob] of stems) {
        let idx = 0;
        for (let i = 0; i < blob.size; i += CHUNK_SIZE, idx++) {
          tasks.push({ stem, index: idx, part: blob.slice(i, i + CHUNK_SIZE) });
        }
        chunkCounts[stem] = idx;
      }
      await uploadChunks(session.upload_id, tasks, setProgress);

      const fd = new FormData();
      fd.append('upload_id', session.upload_id);
      fd.append('name', name.trim());
      fd.append('mixdown_chunks', String(chunkCounts.mixdown));
      if (vocalBlob) fd.append('vocal_chunks', String(chunkCounts.vocal));
      if (instrumentalBlob) {
        fd.append('instrumental_chunks', String(chunkCounts.instrumental));
      } else if (reusedInstrumentalId) {
        fd.append('instrumental_id', reusedInstrumentalId);
      }
      const res = await api.finalizeStudioExport(fd);
      toast.success('Stems saved to your Studio cloud');
      setName('');
      onOpenChange(false);
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed. Please try again.');
    } finally {
      setPhase(null);
      setProgress(0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !phase && onOpenChange(v)}>
      <DialogContent className="bg-surface border-[#262626] max-w-sm" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle>Export Stems</DialogTitle>
          <DialogDescription className="text-xs text-[#888]">
            Saves a time-aligned mixdown plus any recorded vocal and loaded
            instrumental as 320kbps MP3s to your Studio cloud so you can
            download them or deliver to orders.
            {(engine.reverb > 0 || engine.delay > 0) && (
              <span className="block mt-1.5 text-sky-400" data-testid="export-reverb-note">
                {[
                  engine.reverb > 0 ? `Reverb ${Math.round(engine.reverb * 100)}%` : null,
                  engine.delay > 0 ? `Echo ${Math.round(engine.delay * 100)}%` : null,
                ].filter(Boolean).join(' + ')} will be baked into the mixdown — the vocal stem stays dry.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Session name (e.g. Big Match Dub — Selecta K)"
            maxLength={120}
            className="bg-[#0a0a0a] border-[#2a2a2a]"
            data-testid="export-name-input"
          />
          <Button onClick={handleExport} disabled={!!phase} className="btn-primary w-full" data-testid="export-confirm-btn">
            {phase === 'rendering' && <><Loader2 className="w-4 h-4 animate-spin mr-2" />Rendering stems…</>}
            {phase === 'encoding' && <><Loader2 className="w-4 h-4 animate-spin mr-2" />Encoding MP3s…</>}
            {phase === 'uploading' && <><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading… {progress}%</>}
            {!phase && 'Export & Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
